import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUsers, FiClock, FiShield, FiCheck, FiPhone } = FiIcons;

const ServicesPage = () => {
  const services = [
    {
      icon: FiHome,
      title: "Complete Apartment Painting",
      description: "Full interior painting services for apartments in Burbank, CA including walls, ceilings, and trim work.",
      features: ["Interior wall painting", "Ceiling painting", "Trim and baseboard painting", "Color consultation"]
    },
    {
      icon: FiUsers,
      title: "Rental Property Painting",
      description: "Specialized painting services designed for landlords and property managers in Burbank.",
      features: ["Tenant-friendly scheduling", "Durable paint selection", "Quick turnaround", "Property management coordination"]
    },
    {
      icon: FiClock,
      title: "Apartment Turnover Painting",
      description: "Fast and efficient painting services to prepare apartments for new tenants.",
      features: ["24-48 hour completion", "Move-in ready finish", "Touch-up services", "Emergency availability"]
    },
    {
      icon: FiShield,
      title: "Commercial Apartment Painting",
      description: "Large-scale painting projects for apartment complexes and multi-unit buildings.",
      features: ["Bulk pricing discounts", "Project management", "Coordinated scheduling", "Quality assurance"]
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Professional Apartment Painting Services in Burbank, CA
            </h1>
            <p className="text-xl max-w-3xl mx-auto">
              Comprehensive apartment painting solutions for rental properties, apartment complexes, and individual units. Hue Painting Pro delivers quality results on time and on budget.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                    <SafeIcon icon={service.icon} className="h-6 w-6 text-blue-700" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Apartment Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional apartment painters in Burbank follow a proven process to ensure quality results every time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Free Consultation",
                description: "We assess your apartment painting needs and provide a detailed quote."
              },
              {
                step: "2",
                title: "Preparation",
                description: "Professional surface preparation and protection of furniture and floors."
              },
              {
                step: "3",
                title: "Quality Painting",
                description: "Expert application using premium paints and professional techniques."
              },
              {
                step: "4",
                title: "Final Inspection",
                description: "Thorough quality check and touch-ups to ensure perfect results."
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-blue-700 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Contact Hue Painting Pro in Burbank, CA for professional painting services and a free estimate.
            </p>
            <a
              href="tel:+1-818-333-6892"
              className="bg-white text-blue-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="h-5 w-5" />
              <span>Call (818) 333-6892</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;